import Edit16 from '@carbon/icons-react/es/edit/16';
import Purchase16 from '@carbon/icons-react/es/purchase/16';
import { faAddressCard } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
} from 'ag-grid-community';
import { AgGridColumn } from 'ag-grid-react';
import { Tooltip, Typography } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { ProjectId, Role, RoleType, Team, User } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import { Menu } from 'components/atoms/dropdown';
import { EmptyState } from 'components/atoms/empty-state';
import { Grid } from 'components/atoms/grid';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { toastService } from 'components/molecules/toast';
import { NameCell } from 'components/organisms/timesheets/common/cells/name-cell';
import { RoutePaths } from 'constant';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useHasAccess } from 'hooks/use-has-access';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useAccountSettingsTeamsWsUpdates } from 'hooks/websockets';
import { useGetAllPartnerCompanies } from 'queries/partner';
import {
  getTeamsInAccountQueryKey,
  useDeletePlaceholderMutation,
  useDeletePlaceholdersByRoleMutation,
} from 'queries/placeholder';
import { ROLES_QUERY_KEY, useDeleteRoleMutation } from 'queries/roles';
import { removeProjectPlaceholder } from 'stores/memberships';
import { remove as removeTeam } from 'stores/teams';
import { RoleSettingsTab } from './types';

const StyledGrid = styled(Grid)`
  padding-bottom: var(--layout-05);
  .ag-row {
    border-bottom: 1px solid;
    border-color: var(--table-border-color) !important;
  }
  .ag-row-focus {
    background: none;
  }
  .ag-row-hover {
    background: var(--ag-row-hover-color);
  }

  /**
   * Temporary hack to hide the duplicate component rendered
   * Ref: https://github.com/ag-grid/ag-grid/issues/3731
   */
  .ag-react-container > :not(:first-child) {
    display: none !important;
  }
  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
    align-items: flex-start;
    .ag-react-container {
      padding-left: var(--layout-06);
      padding-top: calc(
        calc(var(--layout-06) + var(--layout-03)) + var(--layout-02)
      );
    }
  }
`;

const MoreOptionsMenuDivider = styled(Menu.Divider)`
  margin-left: var(--spacing-05) !important;
  margin-right: var(--spacing-05) !important;
`;

const PlaceholderCellRenderer: FC<ICellRendererParams> = ({ data }) => (
  <span>{data.use_as_placeholder ? 'Yes' : 'No'}</span>
);
const UsedByPartnerCellRenderer: FC<ICellRendererParams> = ({ data }) => (
  <span>{data.usedByPartners ? 'Yes' : 'No'}</span>
);

const RoleCellRenderer: FC<ICellRendererParams> = ({ data }) => {
  return (
    <NameCell
      logo={<Avatar name={data.role} />}
      primaryText={<span>{data.role}</span>}
    />
  );
};

const MoreOptionsCellRenderer: FC<
  ICellRendererParams & { openCostRateManager: (id: number) => void }
> = ({ data, openCostRateManager }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const dispatch = useDispatch();

  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);
  const [accountability] = useAccountFeature(AccountFeature.Accountability);
  const { hasAccess: canReadCostRates } = useHasAccess({
    permissions: ['READ'],
    resource: 'costRate',
  });
  const canViewCostRates =
    accountability && isFinancialsEnabled && canReadCostRates;

  const { mutateAsync: deletePlaceholder } = useDeletePlaceholderMutation(
    data.defaultPlaceholderId,
    {
      onSuccess: async () => {
        dispatch(removeTeam(data.defaultPlaceholderId));
        queryClient.setQueryData<Team[]>(
          getTeamsInAccountQueryKey(),
          (oldValues = []) => [
            ...oldValues.filter(
              ({ teamId }) => teamId !== data.defaultPlaceholderId,
            ),
          ],
        );
      },
    },
  );
  const { mutateAsync: deletePlaceholdersByTeam } =
    useDeletePlaceholdersByRoleMutation({
      onSuccess(_, { roleId: removedRoleId }) {
        const teamsForRole = (
          queryClient.getQueryData<Team[]>(getTeamsInAccountQueryKey()) ?? []
        ).filter(({ roleId: teamRoleId }) => teamRoleId === removedRoleId);

        teamsForRole.forEach(({ teamId, projectId }) => {
          dispatch(removeTeam(teamId));
          if (projectId != null) {
            dispatch(
              removeProjectPlaceholder({
                projectId: ProjectId(projectId),
                teamId,
              }),
            );
          }
        });
        queryClient.setQueryData<Team[]>(
          getTeamsInAccountQueryKey(),
          (oldValues = []) => [
            ...oldValues.filter(
              ({ roleId: teamRoleId }) => teamRoleId !== removedRoleId,
            ),
          ],
        );
        queryClient.invalidateQueries({ queryKey: ['placeholders'] });
      },
    });

  const { mutateAsync: deleteRole } = useDeleteRoleMutation(data.id, {
    onSuccess: async () => {
      toastService({
        title: translate('settings.role.deleted.success.title'),
        message: translate('settings.role.deleted.success.message'),
      }).success();

      queryClient.invalidateQueries(ROLES_QUERY_KEY);
    },
  });
  const deleteConfirm = useDeleteConfirmModal({
    title: useTranslation('settings.roles.delete.confirm.title'),
    content: useTranslation('settings.roles.delete.confirm.message', {
      roleName: data.role,
      b: (chunks) => <Typography.Text strong>{chunks}</Typography.Text>,
    }),
    onOk: async () => {
      if (data.use_as_placeholder) {
        await deletePlaceholder();
        await deletePlaceholdersByTeam({ roleId: data.id });
      }
      await deleteRole();
    },
  });

  const onEdit = useCallback(() => {
    history.push(RoutePaths.editRole.replace(/:roleId/, data.id));
  }, [data.id, history]);

  const menu = (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      <Menu.Item key="edit" icon={<Edit16 />} onClick={onEdit}>
        <FormattedMessage id="settings.roles.edit" />
      </Menu.Item>
      {canViewCostRates && data.type !== RoleType.Customer ? (
        <Menu.Item
          key="manageCostRate"
          icon={<Purchase16 />}
          onClick={() => {
            openCostRateManager(data.id);
          }}
        >
          <FormattedMessage id="settings.cost_rates.title" />
        </Menu.Item>
      ) : null}
      <MoreOptionsMenuDivider />
      <Menu.ItemDelete
        key="delete"
        onClick={deleteConfirm}
        disabled={data.members > 0 && data.type !== RoleType.Customer}
      >
        {data.members > 0 && data.type !== RoleType.Customer ? (
          <Tooltip
            placement="left"
            title={
              <FormattedMessage id="settings.roles.delete.disabled.tooltip" />
            }
          >
            <div>
              <FormattedMessage id="settings.roles.delete" />
            </div>
          </Tooltip>
        ) : (
          <FormattedMessage id="settings.roles.delete" />
        )}
      </Menu.ItemDelete>
    </Menu>
  );

  return <MoreActionsDropdown menu={menu} />;
};
const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  color: var(--gray-30);
`;

const ListEmptyState: FC<{ view: RoleSettingsTab }> = ({ view }) => {
  return (
    <EmptyState
      image={<StyledFontAwesomeIcon icon={faAddressCard} size="4x" />}
      title={<FormattedMessage id="settings.roles.list.empty.title" />}
      message={
        <FormattedMessage id={`settings.roles.list.empty.message.${view}`} />
      }
    />
  );
};

interface Props {
  roles: Role[];
  users: User[];
  placeholders: { [id: number]: Team };
  view: RoleSettingsTab;
  openCostRateManager: (id: number) => void;
}

export const RolesList: FC<Props> = ({
  roles,
  users,
  placeholders,
  view,
  openCostRateManager,
}) => {
  // subscribe for web socket updates on teams add, delete and update
  useAccountSettingsTeamsWsUpdates();
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const { data: partners } = useGetAllPartnerCompanies();
  const useAsPlaceholderText = useTranslation(
    'settings.roles.list.use_as_placeholder',
  );
  const usedByPartnersText = useTranslation(
    'settings.roles.list.use_by_partners',
  );
  const showUsedByPartnersColumn = useMemo(
    () =>
      view !== RoleSettingsTab.Customer &&
      ((partners?.length ?? 0) > 0 ||
        !!roles.find((role) => role.usedByPartners === true)),
    [partners?.length, roles, view],
  );

  const usersCountByRole = useMemo(() => {
    const result: { [roleId: number]: number } = {};

    users.forEach((_user) => {
      const userRoleId = _user.role?.roleId;

      if (!userRoleId) {
        return;
      }
      if (result[userRoleId]) {
        result[userRoleId] += 1;
      } else {
        result[userRoleId] = 1;
      }
    });

    return result;
  }, [users]);

  const placeholderLookup = useMemo(() => {
    const result: { [roleId: number]: Team } = {};

    Object.values(placeholders).forEach((_placeholder) => {
      const _placeholderRole = _placeholder.roleId;

      if (!_placeholderRole) {
        return;
      }

      if (!result[_placeholderRole]) {
        result[_placeholderRole] = _placeholder;
      }
    });

    return result;
  }, [placeholders]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  useEffect(() => {
    if (!gridApi) {
      return;
    }

    gridApi.setRowData(
      Object.values(roles).map((_role) => ({
        id: _role.roleId,
        role: _role.roleName,
        members: usersCountByRole[_role.roleId] ?? 0,
        usedByPartners: _role.usedByPartners,
        use_as_placeholder: Boolean(placeholderLookup[_role.roleId]),
        defaultPlaceholderId: placeholderLookup[_role.roleId]?.teamId,
        type: _role.type,
      })),
    );
  }, [gridApi, roles, usersCountByRole, placeholderLookup]);

  return (
    <StyledGrid
      suppressContextMenu
      suppressCellSelection
      onGridReady={onGridReady}
      frameworkComponents={{
        moreOptionsCellRenderer: MoreOptionsCellRenderer,
        placeholderCellRenderer: PlaceholderCellRenderer,
        roleCellRenderer: RoleCellRenderer,
        UsedByPartnerCellRenderer,
        ListEmptyState,
      }}
      noRowsOverlayComponentParams={{ view }}
      noRowsOverlayComponent="ListEmptyState"
      defaultColDef={{
        filter: false,
        resizable: false,
        suppressMovable: true,
        suppressMenu: true,
        editable: false,
        rowDrag: false,
      }}
    >
      <AgGridColumn
        field="role"
        flex={1}
        sortable
        cellRenderer="roleCellRenderer"
      />
      <AgGridColumn field="members" flex={1} sortable />
      <AgGridColumn
        headerName={useAsPlaceholderText}
        field="use_as_placeholder"
        flex={1}
        cellRenderer="placeholderCellRenderer"
      />
      <AgGridColumn
        headerName={usedByPartnersText}
        field="usedByPartners"
        flex={1}
        hide={!showUsedByPartnersColumn}
        cellRenderer="UsedByPartnerCellRenderer"
      />
      <AgGridColumn
        width={48}
        cellRenderer="moreOptionsCellRenderer"
        cellRendererParams={{ openCostRateManager }}
      />
    </StyledGrid>
  );
};
